import * as THREE from 'three';
import { Vector3 } from '../types/Vector3';
import { Color } from '../types/Color';
import { LayouterType } from '../layout/LayoutData';
import { Animation } from '../animations/Animation';
import { Logger } from '../core/Logger';

/**
 * ProcessCurve - Visualizes the process flow path with numbered markers
 * 
 * Features:
 * - Smooth curve connecting POI positions
 * - Numbered markers (1, 2, 3, ...) at each POI position
 * - Morphing animation when layout changes
 * - Separate from debug visualization system
 */
export class ProcessCurve {
    private _scene: THREE.Scene | null = null;
    private _curvePath: THREE.CatmullRomCurve3 | null = null;
    private _curveGroup: THREE.Group;
    private _curveLine: THREE.Line | null = null;
    private _numberMarkers: THREE.Group[] = [];
    private _currentLayout: LayouterType | null = null;
    private _originalPositions: THREE.Vector3[] = [];
    
    // Animation properties
    private _targetCurvePath: THREE.CatmullRomCurve3 | null = null;
    private _morphAnimation: { 
        startTime: number; 
        duration: number; 
        onUpdate?: (progress: number) => void;
        onComplete?: () => void;
    } | null = null;
    private _isAnimating: boolean = false;
    
    // Styling properties
    private _curveColor: Color = new Color('#ffffff');
    private _curveThickness: number = 0.15;
    private _markerSize: number = 0.3;
    private _numberColor: Color = new Color('#000000');
    private _visible: boolean = true;

    private _showMarkerSpheres: boolean = true;
    private _fontSize: number = 48;
    private _fontFamily: string = 'Arial';
    private _fontWeight: string = 'bold';
    private _lineOffset: number = 0;
    private _billboardScale: number = 1.0;
    private _billboardBackgroundColor: Color = new Color('#ffffff');
    private _billboardBorderColor: Color = new Color('#000000');
    private _showStartMarker: boolean = true;
    private _startMarkerText: string = 'S';
    
    constructor() {
        this._curveGroup = new THREE.Group();
        this._curveGroup.name = 'ProcessCurve';
    }
    
    /**
     * Set the scene for rendering
     */
    public setScene(scene: THREE.Scene): void {
        // Remove from old scene
        if (this._scene && this._curveGroup.parent) {
            this._scene.remove(this._curveGroup);
        }
        
        this._scene = scene;
        
        // Add to new scene
        if (this._scene && this._visible) {
            this._scene.add(this._curveGroup);
        }
    }
    
    /**
     * Update curve based on POI positions and layout type
     */
    public updateCurve(positions: Vector3[], layoutType: LayouterType, animate: boolean = true): void {
        if (positions.length < 2) {
            this.hide();
            return;
        }
        
        //Logger.Instance.Info(`🛤️ ProcessCurve: Updating curve with ${positions.length} positions for ${layoutType} layout`, 'ProcessCurve');
        
        // Convert positions to THREE.Vector3
        const threePositions = positions.map(pos => new THREE.Vector3(pos.x, pos.y, pos.z));
        
        // Create new curve path
        const newCurvePath = this.createCurveForLayout(threePositions, layoutType);
        
        if (animate && this._curvePath && this._currentLayout !== layoutType) {
            // Animate transition
            this.animateToNewCurve(newCurvePath, layoutType);
        } else {
            // Immediate update
            this.setCurveImmediate(newCurvePath, layoutType);
        }
        
        // Update numbered markers
        this.updateNumberMarkers(threePositions);
        
        this._currentLayout = layoutType;
    }
    
    /**
     * Create curve path based on layout type
     */
    private createCurveForLayout(positions: THREE.Vector3[], layoutType: LayouterType): THREE.CatmullRomCurve3 {
        switch (layoutType) {
            case LayouterType.Line:
                // For line layout, create straight path
                return new THREE.CatmullRomCurve3(positions, false, 'centripetal');
                
            case LayouterType.Circle:
                // For circle layout, close the curve
                const circlePoints = [...positions, positions[0]]; // Close the loop
                return new THREE.CatmullRomCurve3(circlePoints, true, 'centripetal');
                
            case LayouterType.Spiral:
                // For spiral, use smooth catmull-rom
                return new THREE.CatmullRomCurve3(positions, false, 'catmullrom');
                
            case LayouterType.Grid:
                // For grid, create snake-like path
                return this.createSnakePath(positions);
                
            default:
                // Default smooth curve
                return new THREE.CatmullRomCurve3(positions, false, 'centripetal');
        }
    }
    
    /**
     * Create snake-like path for grid layouts
     */
    private createSnakePath(positions: THREE.Vector3[]): THREE.CatmullRomCurve3 {
        // TODO: Implement intelligent snake path for grid
        // For now, use simple sequential connection
        return new THREE.CatmullRomCurve3(positions, false, 'centripetal');
    }
    
    /**
     * Set curve immediately without animation
     */
    private setCurveImmediate(curvePath: THREE.CatmullRomCurve3, layoutType: LayouterType): void {
        this._curvePath = curvePath;
        this.updateCurveGeometry();
    }
    
    /**
     * Animate curve transition
     */
    private animateToNewCurve(targetCurve: THREE.CatmullRomCurve3, layoutType: LayouterType): void {
        this._targetCurvePath = targetCurve;
        this._isAnimating = true;
        
        // Create simple morphing animation
        this._morphAnimation = {
            startTime: performance.now(),
            duration: 1500, // 1.5 seconds in milliseconds
            onUpdate: (progress: number) => {
                this.updateMorphProgress(progress);
            },
            onComplete: () => {
                this._curvePath = this._targetCurvePath;
                this._targetCurvePath = null;
                this._isAnimating = false;
                this._morphAnimation = null;
                this.updateCurveGeometry();
                //Logger.Instance.Debug(`🛤️ ProcessCurve: Morph animation complete`, 'ProcessCurve');
            }
        };
        
        //Logger.Instance.Debug(`🛤️ ProcessCurve: Starting morph animation to ${layoutType}`, 'ProcessCurve');
    }
    
    /**
     * Update curve during morphing animation
     */
    private updateMorphProgress(progress: number): void {
        if (!this._curvePath || !this._targetCurvePath) return;
        
        // Interpolate between current and target curve
        const segments = 100;
        const morphedPoints: THREE.Vector3[] = [];
        
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            
            const currentPoint = this._curvePath.getPoint(t);
            const targetPoint = this._targetCurvePath.getPoint(t);
            
            const morphedPoint = new THREE.Vector3().lerpVectors(currentPoint, targetPoint, progress);
            morphedPoints.push(morphedPoint);
        }
        
        // Create temporary curve for morphing
        const morphedCurve = new THREE.CatmullRomCurve3(morphedPoints, false, 'centripetal');
        
        // Update geometry with morphed curve
        this.updateCurveGeometry(morphedCurve);
    }
    
    /**
     * Update the visual curve geometry
     */
    private updateCurveGeometry(curve?: THREE.CatmullRomCurve3): void {
        const activeCurve = curve || this._curvePath;
        if (!activeCurve) return;
        
        // Remove old curve line
        if (this._curveLine) {
            this._curveGroup.remove(this._curveLine);
            this._curveLine.geometry.dispose();
            (this._curveLine.material as THREE.Material).dispose();
        }
        
        // Create new curve geometry
        const points = activeCurve.getPoints(200);
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        
        const material = new THREE.LineBasicMaterial({
            color: new THREE.Color(this._curveColor.r, this._curveColor.g, this._curveColor.b),
            linewidth: this._curveThickness * 2, // Convert to pixel width
            transparent: true,
            opacity: 0.8
        });
        
        this._curveLine = new THREE.Line(geometry, material);
        this._curveLine.name = 'ProcessCurveLine';
        this._curveGroup.add(this._curveLine);
    }
    
    /**
     * Update numbered markers at POI positions with displacement
     */
    private updateNumberMarkers(positions: THREE.Vector3[]): void {
        // Store original positions for future displacement calculations
        this._originalPositions = positions.map(pos => pos.clone());
        
        // Remove old markers
        this._numberMarkers.forEach(marker => {
            this._curveGroup.remove(marker);
            marker.traverse(child => {
                if (child instanceof THREE.Mesh) {
                    child.geometry.dispose();
                    (child.material as THREE.Material).dispose();
                }
            });
        });
        this._numberMarkers = [];
        
        // Create new markers with displacement
        positions.forEach((position, index) => {
            const displacedPosition = this.calculateDisplacedPosition(position, index, positions);
            let markerGroup: THREE.Group;
            
            if (index === 0 && this._showStartMarker) {
                // Create start marker at first position
                markerGroup = this.createTextMarker(this._startMarkerText, displacedPosition);
            } else {
                // Create numbered marker (offset numbering if start marker is shown)
                const markerNumber = this._showStartMarker ? index : index + 1;
                markerGroup = this.createTextMarker(markerNumber.toString(), displacedPosition);
            }
            
            this._numberMarkers.push(markerGroup);
            this._curveGroup.add(markerGroup);
        });
    }
    
    /**
     * Calculate position for marker with line offset
     */
    private calculateDisplacedPosition(position: THREE.Vector3, index: number, allPositions: THREE.Vector3[]): THREE.Vector3 {
        // Start with the original POI position
        const finalPosition = position.clone();
        
        // Apply line offset (interpolate towards next/previous POI)
        if (this._lineOffset !== 0 && allPositions.length > 1) {
            let targetPosition: THREE.Vector3;
            
            if (this._lineOffset > 0) {
                // Positive offset: move towards next POI
                if (index < allPositions.length - 1) {
                    targetPosition = allPositions[index + 1];
                } else {
                    // Last POI: move towards previous POI (reverse direction)
                    targetPosition = allPositions[index - 1];
                }
            } else {
                // Negative offset: move towards previous POI
                if (index > 0) {
                    targetPosition = allPositions[index - 1];
                } else {
                    // First POI: move towards next POI (reverse direction)
                    targetPosition = allPositions[index + 1];
                }
            }
            
            // Linear interpolation along the line
            const offsetVector = new THREE.Vector3().subVectors(targetPosition, position);
            offsetVector.multiplyScalar(Math.abs(this._lineOffset));
            finalPosition.add(offsetVector);
        }
        
        return finalPosition;
    }
    
    /**
     * Create a text marker at given position
     */
    private createTextMarker(text: string, position: THREE.Vector3): THREE.Group {
        const markerGroup = new THREE.Group();
        markerGroup.name = `ProcessMarker_${text}`;
        markerGroup.position.copy(position);
        
        let sphere: THREE.Mesh | undefined;
        
        // Conditionally create sphere background
        if (this._showMarkerSpheres) {
            const sphereGeometry = new THREE.SphereGeometry(this._markerSize * 0.85, 16, 16);
            const sphereMaterial = new THREE.MeshBasicMaterial({
                color: new THREE.Color(this._curveColor.r, this._curveColor.g, this._curveColor.b),
                transparent: true,
                opacity: 0.8,
                depthWrite: true,
                depthTest: true
            });
            sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
            sphere.name = 'MarkerSphere';
            sphere.renderOrder = 1000;
            
            markerGroup.add(sphere);
        }
        
        // Create text with special z-buffer handling
        this.addNumberToMarker(markerGroup, text, sphere);
        
        return markerGroup;
    }
    
    /**
     * Add number text to marker with improved visibility and z-buffer handling
     */
    private addNumberToMarker(markerGroup: THREE.Group, numberText: string, parentSphere?: THREE.Mesh): void {
        // Create canvas for text rendering with higher resolution
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d')!;
        const size = 128; // Higher resolution for better text quality
        canvas.width = size;
        canvas.height = size;
        
        // Clear background
        context.clearRect(0, 0, size, size);
        
        // Draw background circle for better contrast
        context.fillStyle = `rgba(${this._billboardBackgroundColor.r * 255}, ${this._billboardBackgroundColor.g * 255}, ${this._billboardBackgroundColor.b * 255}, 0.9)`;
        context.beginPath();
        context.arc(size/2, size/2, size/2 - 4, 0, Math.PI * 2);
        context.fill();
        
        // Draw border
        context.strokeStyle = `rgba(${this._billboardBorderColor.r * 255}, ${this._billboardBorderColor.g * 255}, ${this._billboardBorderColor.b * 255}, 0.8)`;
        context.lineWidth = 3;
        context.stroke();
        
        // Draw number text with better contrast
        context.fillStyle = `rgb(${this._numberColor.r * 255}, ${this._numberColor.g * 255}, ${this._numberColor.b * 255})`;
        context.font = `${this._fontWeight} ${this._fontSize}px ${this._fontFamily}`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        
        // Add text shadow for better visibility
        context.shadowColor = 'rgba(255, 255, 255, 0.8)';
        context.shadowBlur = 2;
        context.shadowOffsetX = 1;
        context.shadowOffsetY = 1;
        
        context.fillText(numberText, size/2, size/2);
        
        // Create texture and material
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        
        const material = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            alphaTest: 0.1,
            side: THREE.DoubleSide,
            // Normal depth testing for proper occlusion with other objects
            depthTest: true,
            depthWrite: false, // Don't write to depth to avoid interfering with other texts
        });
        
        // Create billboard plane - size based on marker size and billboard scale
        const planeSize = this._markerSize * 1.6 * this._billboardScale;
        const geometry = new THREE.PlaneGeometry(planeSize, planeSize);
        const textMesh = new THREE.Mesh(geometry, material);
        textMesh.name = 'MarkerText';
        
        // Set higher render order than sphere (renders after sphere = on top)
        textMesh.renderOrder = 1001;
        
        // Position text at marker center - it will be larger than the smaller sphere
        textMesh.position.set(0, 0, 0);
        
        // Simple billboard behavior - always face camera
        textMesh.onBeforeRender = (renderer, scene, camera) => {
            textMesh.lookAt(camera.position);
        };
        
        markerGroup.add(textMesh);
    }
    
    /**
     * Update animation (call from main update loop)
     */
    public update(deltaTime: number): void {
        if (this._morphAnimation && this._isAnimating) {
            const currentTime = performance.now();
            const elapsed = currentTime - this._morphAnimation.startTime;
            const progress = Math.min(elapsed / this._morphAnimation.duration, 1.0);
            
            // Apply easing (ease-out)
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            
            if (this._morphAnimation.onUpdate) {
                this._morphAnimation.onUpdate(easedProgress);
            }
            
            // Check if animation is complete
            if (progress >= 1.0) {
                if (this._morphAnimation.onComplete) {
                    this._morphAnimation.onComplete();
                }
            }
        }
    }
    
    /**
     * Show/hide the process curve
     */
    public setVisible(visible: boolean): void {
        this._visible = visible;
        this._curveGroup.visible = visible;
        
        if (this._scene) {
            if (visible && !this._curveGroup.parent) {
                this._scene.add(this._curveGroup);
            } else if (!visible && this._curveGroup.parent) {
                this._scene.remove(this._curveGroup);
            }
        }
    }
    
    /**
     * Hide the curve
     */
    public hide(): void {
        this.setVisible(false);
    }
    
    /**
     * Show the curve
     */
    public show(): void {
        this.setVisible(true);
    }
    
    /**
     * Update styling
     */
    public updateStyle(options: {
        curveColor?: Color;
        curveThickness?: number;
        markerSize?: number;
        numberColor?: Color;

        showMarkerSpheres?: boolean;
        fontSize?: number;
        fontFamily?: string;
        fontWeight?: string;
        lineOffset?: number;
        billboardScale?: number;
        billboardBackgroundColor?: Color;
        billboardBorderColor?: Color;
        showStartMarker?: boolean;
        startMarkerText?: string;
    }): void {
        let needsMarkerUpdate = false;
        
        if (options.curveColor) this._curveColor = options.curveColor;
        if (options.curveThickness) this._curveThickness = options.curveThickness;
        if (options.markerSize) {
            this._markerSize = options.markerSize;
            needsMarkerUpdate = true;
        }
        if (options.numberColor) {
            this._numberColor = options.numberColor;
            needsMarkerUpdate = true;
        }

        if (options.showMarkerSpheres !== undefined) {
            this._showMarkerSpheres = options.showMarkerSpheres;
            needsMarkerUpdate = true;
        }
        if (options.fontSize !== undefined) {
            this._fontSize = options.fontSize;
            needsMarkerUpdate = true;
        }
        if (options.fontFamily !== undefined) {
            this._fontFamily = options.fontFamily;
            needsMarkerUpdate = true;
        }
        if (options.fontWeight !== undefined) {
            this._fontWeight = options.fontWeight;
            needsMarkerUpdate = true;
        }
        if (options.lineOffset !== undefined) {
            this._lineOffset = options.lineOffset;
            needsMarkerUpdate = true;
        }
        if (options.billboardScale !== undefined) {
            this._billboardScale = options.billboardScale;
            needsMarkerUpdate = true;
        }
        if (options.billboardBackgroundColor !== undefined) {
            this._billboardBackgroundColor = options.billboardBackgroundColor;
            needsMarkerUpdate = true;
        }
        if (options.billboardBorderColor !== undefined) {
            this._billboardBorderColor = options.billboardBorderColor;
            needsMarkerUpdate = true;
        }
        if (options.showStartMarker !== undefined) {
            this._showStartMarker = options.showStartMarker;
            needsMarkerUpdate = true;
        }
        if (options.startMarkerText !== undefined) {
            this._startMarkerText = options.startMarkerText;
            needsMarkerUpdate = true;
        }
        
        // Refresh visual elements
        if (this._curvePath) {
            this.updateCurveGeometry();
        }
        
        // Update markers if displacement or styling changed
        if (needsMarkerUpdate && this._originalPositions.length > 0) {
            // Recreate markers using stored original positions
            this.updateNumberMarkers(this._originalPositions);
        }
    }
    
    /**
     * Cleanup resources
     */
    public dispose(): void {
        this.hide();
        
        // Dispose all geometries and materials
        this._curveGroup.traverse(child => {
            if (child instanceof THREE.Mesh) {
                child.geometry.dispose();
                (child.material as THREE.Material).dispose();
            }
        });
        
        this._morphAnimation = null;
        this._curvePath = null;
        this._targetCurvePath = null;
    }
} 